/**
 * "Copy details for the Admin" (epic PI-SPEC-SW-E290, feature
 * PI-SPEC-SW-1478, UX-PM-07; Claude Design frame B2).
 *
 * The page tells a Project Manager what happened and one thing to do, in
 * plain words. Everything a person helping them needs (the error code, the
 * engine's own message, the exact time and the app's versions) goes on the
 * clipboard instead, through this one helper. It is the only place a run
 * message carries an ISO 8601 time; pages show local words
 * (`local-time.ts`). Nothing secret is ever included.
 */

/** What "Copy details for the Admin" puts on the clipboard. */
export interface AdminDetails {
  /** What the person was doing or what failed, in plain words ("A brief could not be finished"). */
  what: string;
  /** The ADO project. */
  project?: string | null | undefined;
  /** The tracker item id, without "AB#". */
  itemId?: string | null | undefined;
  /** The run (queue entry or session run) the failure belongs to. */
  runId?: string | null | undefined;
  /** The engine or API error code; "none" when there is none. */
  errorCode: string | null | undefined;
  /** The engine's or the app's own message, unchanged. */
  rawMessage: string | null | undefined;
  /** When it happened: an ISO 8601 string, a Date or epoch milliseconds; null when nobody recorded it ("unknown"). */
  at: string | Date | number | null;
  /** The SpecTree Toolbox version, when the page runs inside it. */
  appVersion?: string | null | undefined;
  /** The toolset contract numbers the app reports (`bridge.app.contracts`). */
  contracts?: Readonly<Partial<Record<string, number>>> | null | undefined;
}

/** The exact time as ISO 8601 (UTC); an unreadable time is kept as given; null reads "unknown". */
function isoOf(at: string | Date | number | null): string {
  if (at === null) return "unknown";
  const date = at instanceof Date ? at : new Date(at);
  return Number.isNaN(date.getTime()) ? String(at) : date.toISOString();
}

/** The value, or "none" when it is missing or empty. */
function orNone(value: string | null | undefined): string {
  return typeof value === "string" && value.length > 0 ? value : "none";
}

function contractsText(contracts: AdminDetails["contracts"]): string | null {
  if (!contracts) return null;
  const parts = Object.entries(contracts)
    .filter((entry): entry is [string, number] => typeof entry[1] === "number")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([toolset, n]) => `${toolset} ${String(n)}`);
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * The multi-line text "Copy details for the Admin" copies: what happened,
 * where, the code, the raw message, the ISO time and the versions. Lines
 * with nothing to say are left out, except the code and message, which read
 * "none".
 */
export function adminDetailsText(details: AdminDetails): string {
  const lines: string[] = [`What: ${details.what}`];
  if (details.project) lines.push(`Project: ${details.project}`);
  if (details.itemId) lines.push(`Item: AB#${details.itemId}`);
  if (details.runId) lines.push(`Run: ${details.runId}`);
  lines.push(`Error code: ${orNone(details.errorCode)}`);
  lines.push(`Message: ${orNone(details.rawMessage)}`);
  lines.push(`Time: ${isoOf(details.at)}`);
  if (details.appVersion) lines.push(`SpecTree Toolbox: ${details.appVersion}`);
  const contracts = contractsText(details.contracts);
  if (contracts) lines.push(`Contracts: ${contracts}`);
  return lines.join("\n");
}

/**
 * The failed-item card's details (kept for existing callers): a thin wrapper
 * over {@link adminDetailsText}. `at` is when the item failed; without it the
 * time reads "unknown", never the moment of copying.
 */
export function errorDetailsText(details: {
  project: string;
  itemId: string;
  batchId: string;
  errorCode: string | null;
  errorMessage: string | null;
  at?: string | Date | number | null | undefined;
  appVersion?: string | null | undefined;
  contracts?: AdminDetails["contracts"] | undefined;
}): string {
  return adminDetailsText({
    what: "A brief could not be finished",
    project: details.project,
    itemId: details.itemId,
    runId: details.batchId,
    errorCode: details.errorCode,
    rawMessage: details.errorMessage,
    at: details.at ?? null,
    appVersion: details.appVersion,
    contracts: details.contracts,
  });
}
